import { faHouseMedical } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faEdit, faUser } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@repo/videa-components/src/ui/button';
import { useHistory, useLocation } from 'react-router';

import { useUpdateProviderRole } from '@/react-query/insights/useUpdateProviderRole';

import { Breadcrumbs } from '../components/Breadcrumbs';
import { DashboardNavigator } from '../components/DashboardNavigator';
import { Filters } from '../components/filters/Filters';
import {
  DialogHeader,
  DialogTrigger,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogFooter,
  RadioGroup,
  RadioGroupItem,
  Label,
  ToasterContent,
} from '@repo/videa-components';
import { TProviderRole } from '@/react-query/insights/types';
import {
  DialogClose,
  DialogDescription,
} from '@repo/videa-components/src/ui/dialog';
import { useState } from 'react';
import { toast, Toaster } from 'sonner';

export const ProviderHeader = ({
  singleProvider,
}: {
  singleProvider: {
    providerName?: string;
    providerVideaId?: string;
    providerId?: number;
    practiceName?: string;
    practiceId?: string;
    providerRole?: string;
  };
}) => {
  const [providerRole, setProviderRole] = useState<TProviderRole>(
    singleProvider?.providerRole as TProviderRole,
  );

  const location = useLocation();
  const history = useHistory();
  const regionId =
    location.pathname.split('region/')[1]?.split('/')[0] === 'undefined'
      ? undefined
      : location.pathname.split('region/')[1]?.split('/')[0];
  const insightsPath =
    location.pathname.split('insights')[0] +
    'insights/' +
    (location.pathname.includes('hygiene') ? 'hygiene' : 'clinical') +
    '/region/' +
    regionId;

  const handleSuccess = () => {
    toast(
      <ToasterContent
        toast={toast}
        title="Provider role updated"
        description={`This change will sync to Insights overnight, and the provider's data will be updated tomorrow.`}
        buttonVariant="success"
      />,
    );
  };

  const { mutate: updateProviderRole } = useUpdateProviderRole(handleSuccess);

  const handleUpdateProviderRole = () => {
    if (singleProvider?.providerId && providerRole) {
      updateProviderRole({
        providerId: singleProvider?.providerId,
        providerRole: providerRole as TProviderRole,
      });
    }
  };

  return (
    <div className="vai-flex vai-flex-col vai-gap-4 vai-w-full">
      <Breadcrumbs
        providerName={singleProvider?.providerName}
        practiceName={singleProvider?.practiceName}
      />
      <div className="vai-flex vai-flex-col">
        <span className="vai-typography-h4">
          {singleProvider?.providerName}
        </span>

        <div className="vai-flex vai-flex-row vai-gap-1">
          <div className="vai-typography-muted vai-flex vai-flex-row vai-items-center">
            <FontAwesomeIcon icon={faUser} />
            <div className="vai-flex vai-flex-row vai-items-center vai-gap-1 vai-group">
              <span className="vai-typography-muted vai-ml-2">
                {singleProvider?.providerRole
                  ? singleProvider?.providerRole?.[0]?.toUpperCase() +
                    singleProvider?.providerRole?.slice(1).toLowerCase()
                  : 'None'}
              </span>

              <Dialog>
                <DialogTrigger className="vai-opacity-0 group-hover:vai-opacity-100">
                  <Button variant="ghostIcon" size="xs" className="">
                    <FontAwesomeIcon
                      icon={faEdit}
                      className="vai-text-muted-foreground"
                    />
                  </Button>
                </DialogTrigger>
                <DialogContent className="!vai-w-96">
                  <DialogHeader>
                    <DialogTitle>{singleProvider?.providerName}</DialogTitle>
                  </DialogHeader>
                  <DialogDescription>
                    <div className="vai-flex vai-flex-col vai-gap-2">
                      Update {singleProvider?.providerName}&#39;s role to:
                      <RadioGroup
                        defaultValue={singleProvider?.providerRole}
                        onValueChange={(value) => {
                          setProviderRole(value as TProviderRole);
                        }}
                      >
                        <div className="vai-flex vai-flex-row vai-gap-2">
                          <RadioGroupItem value="DENTIST" />
                          <Label>Dentist</Label>
                        </div>
                        <div className="vai-flex vai-flex-row vai-gap-2">
                          <RadioGroupItem value="HYGIENIST" />
                          <Label>Hygienist</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </DialogDescription>
                  <DialogFooter>
                    <DialogClose>
                      <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <DialogClose>
                      <Button
                        variant="default"
                        onClick={handleUpdateProviderRole}
                      >
                        Save
                      </Button>
                    </DialogClose>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {singleProvider?.practiceName && (
            <div className="vai-typography-muted vai-flex vai-flex-row vai-items-center">
              <FontAwesomeIcon icon={faHouseMedical} />
              <Button
                variant="link"
                size="sm"
                onClick={() => {
                  if (
                    singleProvider?.practiceId &&
                    singleProvider?.practiceName
                  ) {
                    history.push({
                      pathname: `${insightsPath}/practice/${singleProvider?.practiceId}`,
                      search: location.search,
                    });
                  }
                }}
                className="vai-font-light vai-underline"
              >
                {singleProvider?.practiceName}
              </Button>
            </div>
          )}
        </div>
      </div>
      {singleProvider?.providerRole !== 'DENTIST' && <DashboardNavigator />}
      <Filters
        selectedDrilldownPractices={
          singleProvider?.practiceId ? [singleProvider.practiceId] : undefined
        }
        selectedDrilldownProviders={
          singleProvider?.providerVideaId
            ? [singleProvider.providerVideaId]
            : undefined
        }
        enableProviderRoleFilter={false}
        enableTasksFilter={location.pathname.includes('hygiene')}
        enableMaterialityFilter={location.pathname.includes('clinical')}
      />
      <Toaster theme="light" />
    </div>
  );
};
